/**
 * Textos «Por qué importa» de los avisos (catálogo 4.1 de Carlos): «los textos son valores de
 * fábrica… y el administrador puede ajustarlos como la severidad». Por hito, relación e idioma;
 * vacío = el texto de fábrica. Todos los usuarios de la org los ven; solo el admin los cambia.
 */
import { useEffect, useMemo, useState } from "react";
import { ConfirmDialog } from "./ui";
import { api } from "../lib/api";
import { useLang, useT } from "../i18n";
import { groupColor, useShowHitoCode } from "../lib/hitos";

type Lg = "es" | "en";
type Rel = "propio" | "competencia";
type Cell = { aplica: boolean; fabrica: Record<Lg, string | null>; org: Record<Lg, string | null> };
type Row = { id: string; nombre: string; grupo: number; cubierto: boolean; propio: Cell; competencia: Cell };
type Resp = { editable: boolean; items: Row[] };

const MAX = 600;

export default function AlertTextsPanel({ q, onlyCovered }: { q: string; onlyCovered: boolean }) {
  const t = useT();
  const showCode = useShowHitoCode();
  const userLang = useLang();
  const [lg, setLg] = useState<Lg>(userLang === "en" ? "en" : "es");
  const [data, setData] = useState<Resp | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [confirmRestore, setConfirmRestore] = useState(false);

  const load = () => api.get<Resp>("/hitos/textos").then(setData).catch(e => setErr(String(e?.message ?? e)));
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.items ?? []).filter(r => (!onlyCovered || r.cubierto)
      && (!needle || r.id.toLowerCase().includes(needle) || r.nombre.toLowerCase().includes(needle)));
  }, [data, q, onlyCovered]);

  const key = (r: Row, rel: Rel) => `${r.id}-${rel}-${lg}`;
  const save = async (r: Row, rel: Rel, texto: string | null) => {
    setErr(null);
    try {
      await api.put(`/hitos/${r.id}/texto`, { relacion: rel, lang: lg, texto });
      setDrafts(d => { const n = { ...d }; delete n[key(r, rel)]; return n; });
      setSaved(key(r, rel));
      setTimeout(() => setSaved(null), 1500);
      await load();
    } catch (e: unknown) {
      setErr(String((e as Error)?.message ?? e));
    }
  };

  const cell = (r: Row, rel: Rel) => {
    const c = r[rel];
    if (!c.aplica) return <span style={{ color: "var(--clr-text4)", fontSize: "0.75rem" }}>{t("hitos.sevNa")}</span>;
    const efectivo = c.org[lg] ?? c.fabrica[lg] ?? "";
    const k = key(r, rel);
    const draft = drafts[k];
    const value = draft ?? efectivo;
    const dirty = draft !== undefined && draft.trim() !== efectivo;
    if (!data?.editable) {
      return <span style={{ fontSize: "0.8rem", lineHeight: 1.45, fontWeight: c.org[lg] ? 600 : 400 }}>{efectivo}</span>;
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <textarea value={value} maxLength={MAX} rows={3}
          onChange={e => setDrafts(d => ({ ...d, [k]: e.target.value }))}
          style={{ resize: "vertical", fontSize: "0.8rem", fontFamily: "inherit", lineHeight: 1.45,
                   background: "var(--clr-input-bg)", color: "var(--clr-text)", padding: 8, borderRadius: 8,
                   border: `1px solid ${c.org[lg] ? "var(--clr-org-primary)" : "var(--clr-border)"}`, minWidth: 260 }} />
        <span style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", alignItems: "center", fontSize: "0.68rem",
                       color: "var(--clr-text4)", whiteSpace: "nowrap" }}>
          {dirty && (
            <button onClick={() => save(r, rel, value)}
              style={{ background: "var(--clr-org-primary)", color: "#fff", border: 0, borderRadius: 999, padding: "3px 10px",
                       fontSize: "0.7rem", fontWeight: 600, cursor: "pointer" }}>{t("hitos.txtSave")}</button>
          )}
          {dirty && (
            <button onClick={() => setDrafts(d => { const n = { ...d }; delete n[k]; return n; })}
              style={{ background: "none", border: 0, padding: 0, color: "var(--clr-text4)", cursor: "pointer", fontSize: "0.68rem" }}>
              {t("hitos.txtCancel")}</button>
          )}
          {!dirty && c.org[lg] && (
            <>
              <span>{t("hitos.txtCustom")}</span>
              <button onClick={() => save(r, rel, null)} title={c.fabrica[lg] ?? ""}
                style={{ background: "none", border: 0, padding: 0, color: "var(--clr-org-primary)", cursor: "pointer", fontSize: "0.68rem" }}>
                {t("hitos.txtRestore")}</button>
            </>
          )}
          {saved === k && <span style={{ color: "#16803f" }}>{t("hitos.sevSaved")}</span>}
          <span style={{ marginLeft: "auto" }}>{value.length}/{MAX}</span>
        </span>
      </div>
    );
  };

  // Restaurar todo (feedback de Carlos, 20 oct 2026): los textos de fábrica en los dos idiomas.
  const customCount = (data?.items ?? []).reduce((n, r) =>
    n + [r.propio, r.competencia].reduce((m, c) => m + (c.org.es ? 1 : 0) + (c.org.en ? 1 : 0), 0), 0);
  const restoreAll = async () => {
    setConfirmRestore(false); setErr(null);
    try { await api.delete("/hitos/textos"); setDrafts({}); await load(); } catch (e: unknown) { setErr(String((e as Error)?.message ?? e)); }
  };

  const pill = (active: boolean): React.CSSProperties => ({
    fontSize: "0.75rem", padding: "4px 12px", borderRadius: 999, cursor: "pointer", fontWeight: 600,
    border: "1px solid var(--clr-border)", background: active ? "var(--clr-org-primary)" : "var(--clr-surface)",
    color: active ? "#fff" : "var(--clr-text3)",
  });

  return (
    <>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "0 0 12px" }}>
        <span style={{ fontSize: "0.78rem", color: "var(--clr-text3)" }}>{t("hitos.txtLanguage")}</span>
        <button style={pill(lg === "es")} onClick={() => setLg("es")}>Español</button>
        <button style={pill(lg === "en")} onClick={() => setLg("en")}>English</button>
        {err && <span style={{ color: "#dc2626", fontSize: "0.8rem" }}>{err}</span>}
        {data?.editable && customCount > 0 && (
          <button onClick={() => setConfirmRestore(true)}
            style={{ marginLeft: "auto", background: "transparent", border: "1px solid var(--clr-border)", borderRadius: 999,
                     padding: "6px 14px", fontSize: "0.78rem", fontWeight: 600, color: "var(--clr-text2)", cursor: "pointer" }}>
            {t("hitos.txtRestoreAll", { n: customCount })}
          </button>
        )}
      </div>
      <ConfirmDialog open={confirmRestore} title={t("hitos.txtRestoreAllTitle")} message={t("hitos.txtRestoreAllMsg", { n: customCount })}
        confirmLabel={t("hitos.restoreConfirm")} danger={false} onConfirm={restoreAll} onCancel={() => setConfirmRestore(false)} />
      <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, overflow: "hidden",
                    boxShadow: "var(--clr-shadow-sm)" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                {[t("hitos.colHito"), t("hitos.txtColPropio"), t("hitos.txtColCompetencia")].map(h => (
                  <th key={h} style={{ padding: "0.7rem 12px", fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase",
                                       letterSpacing: 0.9, color: "var(--clr-text4)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderTop: "1px solid var(--clr-border2)" }}>
                  <td style={{ padding: "9px 12px", maxWidth: 260, verticalAlign: "top" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      {showCode ? (
                        <span style={{ background: groupColor(r.grupo) + "1c", color: groupColor(r.grupo), padding: "2px 7px", borderRadius: 999,
                                       fontSize: "0.7rem", fontWeight: 700, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{r.id}</span>
                      ) : (
                        <span aria-hidden style={{ width: 8, height: 8, borderRadius: 999, background: groupColor(r.grupo), flex: "none" }} />
                      )}
                      <span>{r.nombre}</span>
                    </span>
                  </td>
                  <td style={{ padding: "9px 12px", verticalAlign: "top" }}>{cell(r, "propio")}</td>
                  <td style={{ padding: "9px 12px", verticalAlign: "top" }}>{cell(r, "competencia")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
